import { AnalyticsEventName } from '@dupe/common/constants'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useEffect, useRef } from 'react'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import logger from '~/src/lib/logger'
import { validateStripeSession } from '~/src/lib/stripe'
import {
  OnboardingStep,
  useBillingPeriod,
  useOnboardingActions,
} from '~/src/stores/useOnboardingStore'

interface Props {
  setProcessing: Dispatch<SetStateAction<boolean>>
  setErrorMessage: Dispatch<SetStateAction<string | null>>
}

const useValidateStripeSession = ({
  setProcessing,
  setErrorMessage,
}: Props) => {
  const analytics = useAnalytics()
  const router = useRouter()
  const searchParams = useSearchParams()

  const { authUser } = useAuthUser()
  const { refetchUserData, userData, updateUserDetails } = useAuthContext()
  const validateSessionRef = useRef(false)

  const onboardingActions = useOnboardingActions()

  const stripeSessionIdParam = searchParams?.get('session_id') // check if we have a session id from stripe
  const statusParam = searchParams?.get('status')

  const existingSubscription = userData?.metadata?.subscription as {
    billingPeriod: string
    paymentStatus: string
    plan: string
  } | null

  const existingBillingPeriod = useBillingPeriod()
  // Validate stripe session
  useEffect(() => {
    if (!router.isReady) return
    if (!authUser?.uid) return
    if (!userData?.id) return
    if (!stripeSessionIdParam) return
    if (statusParam === 'cancel') return
    if (existingSubscription) return
    if (validateSessionRef.current) return
    validateSessionRef.current = true
    ;(async () => {
      setProcessing(true)

      try {
        const authToken = authUser.accessToken || (await authUser.getIdToken())
        const result = await validateStripeSession(
          stripeSessionIdParam as string,
          authToken
        )

        if (!result.success) {
          setErrorMessage(result.error || 'Error processing payment')
          setProcessing(false)
          return
        }

        const { customerId, subscriptionId, status, paymentStatus, liveMode } =
          result
        const billingPeriod = existingBillingPeriod || 'yearly'

        const subscription = {
          plan: 'pro',
          billingPeriod,
          paymentStatus,
          liveMode,
        }
        const stripeCustomerId = customerId
        const stripeSubscriptionId = subscriptionId

        analytics.track(AnalyticsEventName.AuthModalOnboardingPaywallValidate, {
          success: result.success,
          subscription,
          stripeCustomerId,
          stripeSubscriptionId,
        })

        if (
          stripeCustomerId &&
          stripeSubscriptionId &&
          status === 'complete' &&
          paymentStatus === 'paid'
        ) {
          // move to next step
          onboardingActions.setStepData({
            stripeCustomerId,
            stripeSubscriptionId,
            subscription,
          })

          // Update user details in the database
          await updateUserDetails({
            subscription,
            stripeCustomerId,
            stripeSubscriptionId,
          })

          onboardingActions.markStepComplete(OnboardingStep.PAYWALL)
          onboardingActions.nextStep(OnboardingStep.COMPLETE)
        }
      } catch (error) {
        logger.error('Error validating stripe session:', { error })
      } finally {
        setProcessing(false)
      }
    })()
    // The `analytics` object causes this useEffect to
    // run multiple times, so we exclude it from the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authUser?.uid,
    userData?.id,
    existingSubscription,
    existingBillingPeriod,
    stripeSessionIdParam,
    statusParam,
    onboardingActions,
    refetchUserData,
    router.isReady,
    setProcessing,
    setErrorMessage,
    updateUserDetails,
  ])
}

export default useValidateStripeSession
